import { useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'

/**
 * ArtemisChaseCameraView - Copies WebGPU render target to HTML canvas
 * Uses GPUCommandEncoder.copyTextureToBuffer to extract pixels without post-processing
 */
export function ArtemisChaseCameraView({ renderTarget, isActive, artemisProgress = 0, followModeActive = false }) {
  const canvasRef = useRef(null)
  const staticCanvasRef = useRef(null)
  const windowFrameRef = useRef(null)
  const tintOverlayRef = useRef(null)
  const minimizeBtnRef = useRef(null)
  const gpuBufferRef = useRef(null)
  const isMappingRef = useRef(false)
  const isMountedRef = useRef(true)
  const animationFrameRef = useRef(null)
  // Minimized/expanded state PERSISTS across deactivate→reactivate (e.g. scrubbing
  // the seek bar tears the view down and rebuilds it). A ref, not state, so it
  // survives the [isActive] effect re-running without forcing a re-render. The
  // window stays minimized until the user presses + again.
  const isMinimizedRef = useRef(false)
  
  // Check if we're in signal loss range
  const isSignalLost = (artemisProgress >= 74 && artemisProgress <= 76) || 
                       (artemisProgress >= 98 && artemisProgress <= 100)
  
  useEffect(() => {
    isMountedRef.current = true
    
    if (!isActive) return
    
    const root = document.getElementById('root')
    if (!root) return
    
    // Create main canvas element
    const canvas = document.createElement('canvas')
    canvas.width = 320
    canvas.height = 240
    canvas.className = 'chase-camera-view'
    
    root.appendChild(canvas)
    canvasRef.current = canvas
    
    // Create static overlay canvas - positioned after main canvas exists
    const staticCanvas = document.createElement('canvas')
    staticCanvas.width = 320
    staticCanvas.height = 240
    staticCanvas.className = 'chase-camera-static'
    staticCanvas.style.position = 'fixed'
    staticCanvas.style.pointerEvents = 'none'
    staticCanvas.style.zIndex = '1501'
    staticCanvas.style.opacity = '0'
    staticCanvas.style.transition = 'opacity 0.3s ease'
    
    root.appendChild(staticCanvas)
    staticCanvasRef.current = staticCanvas

    // Window-frame overlay — gives the "looking through Orion's window" impression
    // when the camera is attached to the front of the capsule (follow mode). Sits
    // over the feed (above the canvas, below the minimize button) and tracks the
    // feed's rect via updatePositions(). Shown only in follow mode (separate effect).
    const windowFrame = document.createElement('img')
    windowFrame.src = '/images/OrionWindowFrame_v1.png'
    windowFrame.alt = ''
    windowFrame.className = 'chase-camera-window-frame'
    windowFrame.style.position = 'fixed'
    windowFrame.style.pointerEvents = 'none'
    windowFrame.style.zIndex = '1501'
    windowFrame.style.opacity = '0'
    root.appendChild(windowFrame)
    windowFrameRef.current = windowFrame

    // Red tint overlay — a translucent red gel sheet laid over EVERYTHING in the
    // feed, including the window-frame image above it, for the "tracking Orion"
    // follow-mode look. Appended after windowFrame (and given a higher z-index)
    // so it composites on top of the frame rather than under it. Shown/hidden
    // alongside the frame in the followModeActive effect below.
    const tintOverlay = document.createElement('div')
    tintOverlay.className = 'chase-camera-tint'
    tintOverlay.style.position = 'fixed'
    tintOverlay.style.pointerEvents = 'none'
    tintOverlay.style.zIndex = '1501'
    tintOverlay.style.opacity = '0'
    root.appendChild(tintOverlay)
    tintOverlayRef.current = tintOverlay

    // Create minimize button
    const btn = document.createElement('button')
    btn.className = 'chase-camera-minimize'
    root.appendChild(btn)
    minimizeBtnRef.current = btn

    // Restore the persisted minimized/expanded state onto the freshly-created
    // elements (this effect re-runs whenever isActive toggles, e.g. on scrub).
    const applyMinimizedState = (minimized) => {
      canvas.classList.toggle('minimized', minimized)
      staticCanvas.classList.toggle('minimized', minimized)
      windowFrame.classList.toggle('minimized', minimized)
      tintOverlay.classList.toggle('minimized', minimized)
      root.classList.toggle('chase-camera-minimized', minimized)
      btn.textContent = minimized ? '+' : '−'
      btn.setAttribute('aria-label', minimized ? 'Expand chase camera' : 'Minimize chase camera')
    }
    applyMinimizedState(isMinimizedRef.current)
    
    // Copy position from main canvas
    const updatePositions = () => {
      if (!canvas || !staticCanvas || !btn) return
      const rect = canvas.getBoundingClientRect()
      staticCanvas.style.left = `${rect.left}px`
      staticCanvas.style.top = `${rect.top}px`
      staticCanvas.style.width = `${rect.width}px`
      staticCanvas.style.height = `${rect.height}px`
      windowFrame.style.left = `${rect.left}px`
      windowFrame.style.top = `${rect.top}px`
      windowFrame.style.width = `${rect.width}px`
      windowFrame.style.height = `${rect.height}px`
      tintOverlay.style.left = `${rect.left}px`
      tintOverlay.style.top = `${rect.top}px`
      tintOverlay.style.width = `${rect.width}px`
      tintOverlay.style.height = `${rect.height}px`
      btn.style.left = `${rect.left}px`
      btn.style.top = `${rect.top}px`
    }
    
    // Minimize toggle — flips and PERSISTS the state so it carries across a
    // deactivate→reactivate cycle.
    const toggleMinimize = () => {
      isMinimizedRef.current = !isMinimizedRef.current
      applyMinimizedState(isMinimizedRef.current)
    }
    btn.addEventListener('click', toggleMinimize)
    
    // Initial position update
    const positionTimeoutId = setTimeout(updatePositions, 100)

    // Update position on window resize
    window.addEventListener('resize', updatePositions)

    return () => {
      isMountedRef.current = false

      window.removeEventListener('resize', updatePositions)
      clearTimeout(positionTimeoutId)
      btn.removeEventListener('click', toggleMinimize)
      // Drop the root class on teardown; the next mount re-applies it from the
      // persisted isMinimizedRef so the window + STOP TRACKING button stay in sync.
      root.classList.remove('chase-camera-minimized')

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      if (canvasRef.current) {
        const r = document.getElementById('root')
        if (r && canvasRef.current.parentNode === r) r.removeChild(canvasRef.current)
        else document.body.removeChild(canvasRef.current)
        canvasRef.current = null
      }
      if (staticCanvasRef.current) {
        const r = document.getElementById('root')
        if (r && staticCanvasRef.current.parentNode === r) r.removeChild(staticCanvasRef.current)
        else document.body.removeChild(staticCanvasRef.current)
        staticCanvasRef.current = null
      }
      if (windowFrameRef.current) {
        const r = document.getElementById('root')
        if (r && windowFrameRef.current.parentNode === r) r.removeChild(windowFrameRef.current)
        else if (windowFrameRef.current.parentNode) windowFrameRef.current.parentNode.removeChild(windowFrameRef.current)
        windowFrameRef.current = null
      }
      if (tintOverlayRef.current) {
        const r = document.getElementById('root')
        if (r && tintOverlayRef.current.parentNode === r) r.removeChild(tintOverlayRef.current)
        else if (tintOverlayRef.current.parentNode) tintOverlayRef.current.parentNode.removeChild(tintOverlayRef.current)
        tintOverlayRef.current = null
      }
      if (minimizeBtnRef.current) {
        const r = document.getElementById('root')
        if (r && minimizeBtnRef.current.parentNode === r) r.removeChild(minimizeBtnRef.current)
        else document.body.removeChild(minimizeBtnRef.current)
        minimizeBtnRef.current = null
      }
      if (gpuBufferRef.current) {
        gpuBufferRef.current.destroy()
        gpuBufferRef.current = null
      }
      isMappingRef.current = false
    }
  }, [isActive])
  
  // Animate static effect when signal is lost
  useEffect(() => {
    if (!staticCanvasRef.current || !isActive) return
    
    const staticCanvas = staticCanvasRef.current
    const staticCtx = staticCanvas.getContext('2d')
    
    if (isSignalLost) {
      staticCanvas.style.opacity = '1'
      
      const drawStatic = () => {
        if (!isMountedRef.current || !isSignalLost) return
        
        const width = staticCanvas.width
        const height = staticCanvas.height
        const imageData = staticCtx.createImageData(width, height)
        const data = imageData.data
        const dataLength = data.length
        
        // Pre-calculate values outside loops
        const time = Date.now() * 0.001
        const glitchIntensity = Math.sin(time * 3) * 0.5 + 0.5
        const glitchThreshold = 0.15 * glitchIntensity
        
        // Optimized: Single pass through pixels with reduced calculations
        let i = 0
        for (let y = 0; y < height; y++) {
          // Pre-calculate per-row values
          const isEvenRow = (y & 1) === 0 // Bitwise AND faster than modulo
          const scanLineMod = isEvenRow ? 0.85 : 1.0
          const isGlitchLine = Math.random() < glitchThreshold
          const lineShift = isGlitchLine ? ((Math.random() * 30 - 15) | 0) : 0 // | 0 faster than Math.floor
          
          for (let x = 0; x < width; x++, i += 4) {
            // Base noise value
            const baseValue = (30 + Math.random() * 60) * scanLineMod
            
            if (isGlitchLine && x + lineShift >= 0 && x + lineShift < width) {
              // Glitch line with RGB separation (simplified)
              const rand = Math.random()
              data[i] = rand < 0.3 ? 255 : baseValue
              data[i + 1] = baseValue * 0.9
              data[i + 2] = rand > 0.7 ? 255 : baseValue * 1.1
            } else {
              // Normal grayscale
              data[i] = data[i + 1] = data[i + 2] = baseValue
            }
            data[i + 3] = 255
          }
        }
        
        // Reduced frequency block glitches (10% instead of 20%)
        if (Math.random() < 0.1) {
          const blockX = (Math.random() * width) | 0
          const blockY = (Math.random() * height) | 0
          const blockW = (Math.random() * 30 + 15) | 0
          const blockH = (Math.random() * 15 + 5) | 0
          const maxY = Math.min(blockY + blockH, height)
          const maxX = Math.min(blockX + blockW, width)
          
          for (let y = blockY; y < maxY; y++) {
            const rowOffset = y * width * 4
            for (let x = blockX; x < maxX; x++) {
              const i = rowOffset + x * 4
              const rand = Math.random()
              data[i] = rand < 0.5 ? 255 : 0
              data[i + 1] = rand < 0.5 ? 255 : 0
              data[i + 2] = rand > 0.5 ? 255 : 0
            }
          }
        }
        
        staticCtx.putImageData(imageData, 0, 0)
        
        // Add "SIGNAL LOST" text with digital glitch
        staticCtx.fillStyle = '#EE3A17'
        staticCtx.font = 'bold 20px monospace'
        staticCtx.textAlign = 'center'
        staticCtx.textBaseline = 'middle'
        
        // Digital glitch - random position offset with occasional large jumps
        const glitchX = Math.random() < 0.1 ? (Math.random() - 0.5) * 20 : (Math.random() - 0.5) * 4
        const glitchY = Math.random() < 0.1 ? (Math.random() - 0.5) * 20 : (Math.random() - 0.5) * 4
        
        // Draw with RGB separation occasionally
        if (Math.random() < 0.3) {
          staticCtx.fillStyle = 'rgba(255, 0, 0, 0.7)'
          staticCtx.fillText('SIGNAL LOST', staticCanvas.width / 2 + glitchX - 2, staticCanvas.height / 2 + glitchY)
          staticCtx.fillStyle = 'rgba(0, 255, 255, 0.7)'
          staticCtx.fillText('SIGNAL LOST', staticCanvas.width / 2 + glitchX + 2, staticCanvas.height / 2 + glitchY)
        } else {
          staticCtx.fillStyle = '#EE3A17'
          staticCtx.fillText('SIGNAL LOST', staticCanvas.width / 2 + glitchX, staticCanvas.height / 2 + glitchY)
        }
        
        // Continue animation
        animationFrameRef.current = requestAnimationFrame(drawStatic)
      }
      
      drawStatic()
    } else {
      staticCanvas.style.opacity = '0'
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [isSignalLost, isActive])
  
  // Show the Orion window-frame overlay only while the camera is attached to the
  // front of the capsule (follow mode). The CSS .minimized class still forces it
  // to opacity 0 when the window is collapsed, so this only governs the
  // follow-mode show.
  useEffect(() => {
    const frame = windowFrameRef.current
    if (!frame) return
    frame.style.opacity = (isActive && followModeActive) ? '1' : '0'
  }, [isActive, followModeActive])

  // The red tint is a permanent fixture of the chase-camera feed — shown any
  // time the feed itself is active, independent of follow mode (unlike the
  // window frame, which only makes sense while attached to Orion's nose).
  useEffect(() => {
    const tint = tintOverlayRef.current
    if (!tint) return
    tint.style.opacity = isActive ? '1' : '0'
  }, [isActive])

  // Copy GPU texture to canvas - no throttling, sRGB handled at render target
  useFrame(({ gl }) => {
    if (!canvasRef.current || !isActive) return
    if (!renderTarget) return
    
    // Skip if already processing - don't queue up operations
    if (isMappingRef.current) return
    
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    
    try {
      // Get the WebGPU backend and device
      const backend = gl.backend
      if (!backend || !backend.device) return
      
      const device = backend.device
      
      // Check if renderTarget has texture property
      if (!renderTarget.texture) return
      
      const gpuTexture = backend.get(renderTarget.texture).texture
      
      if (!gpuTexture) return
      
      // WebGLRenderTarget stores dimensions in .width and .height properties
      const width = renderTarget.width || renderTarget.texture.image?.width || 320
      const height = renderTarget.height || renderTarget.texture.image?.height || 240
      
      // Calculate buffer size with proper alignment (256 bytes for WebGPU)
      const bytesPerPixel = 4 // RGBA
      const unalignedBytesPerRow = width * bytesPerPixel
      const bytesPerRow = Math.ceil(unalignedBytesPerRow / 256) * 256
      const bufferSize = bytesPerRow * height
      
      // Validate values
      if (!Number.isFinite(bufferSize) || bufferSize <= 0) return
      
      // Create or reuse GPU buffer for reading pixels
      if (!gpuBufferRef.current || gpuBufferRef.current.size !== bufferSize) {
        // If buffer exists, wait to destroy it until it's safe
        const oldBuffer = gpuBufferRef.current
        
        // Create new buffer first
        gpuBufferRef.current = device.createBuffer({
          size: bufferSize,
          usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
        })
        
        // Schedule old buffer destruction after any pending operations
        if (oldBuffer) {
          // Destroy asynchronously to avoid conflicts
          setTimeout(() => {
            try {
              if (oldBuffer.mapState === 'mapped') {
                oldBuffer.unmap()
              }
              oldBuffer.destroy()
            } catch (e) {
              // Buffer already destroyed, ignore
            }
          }, 100)
        }
      }
      
      const buffer = gpuBufferRef.current
      
      // Double-check buffer state - skip if not ready
      if (buffer.mapState !== 'unmapped') return
      
      // Mark as busy
      isMappingRef.current = true
      
      // Create command encoder
      const commandEncoder = device.createCommandEncoder()
      
      // Copy texture to buffer
      commandEncoder.copyTextureToBuffer(
        { texture: gpuTexture },
        { buffer, bytesPerRow },
        { width, height }
      )
      
      // Submit commands
      device.queue.submit([commandEncoder.finish()])
      
      // Read buffer asynchronously - don't wait for GPU
      buffer.mapAsync(GPUMapMode.READ).then(() => {
        if (!isMountedRef.current) {
          buffer.unmap()
          isMappingRef.current = false
          return
        }
        
        const arrayBuffer = buffer.getMappedRange()
        const pixels = new Uint8Array(arrayBuffer)
        
        // Create ImageData
        const imageData = ctx.createImageData(width, height)
        const data = imageData.data
        
        // Direct pixel copy - flip vertically, no color correction (sRGB handled at render target)
        let srcY = 0
        let dstY = height - 1
        
        for (let y = 0; y < height; y++, srcY++, dstY--) {
          const srcRowStart = srcY * bytesPerRow
          const dstRowStart = dstY * width * 4
          
          // Direct copy - render target is already in sRGB space
          for (let x = 0; x < width; x++) {
            const srcIdx = srcRowStart + (x * 4)
            const dstIdx = dstRowStart + (x * 4)
            
            // Direct copy - no color correction needed
            data[dstIdx] = pixels[srcIdx]
            data[dstIdx + 1] = pixels[srcIdx + 1]
            data[dstIdx + 2] = pixels[srcIdx + 2]
            data[dstIdx + 3] = 255
          }
        }
        
        // Fill canvas with black background first
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, width, height)
        
        // Draw to canvas
        ctx.putImageData(imageData, 0, 0)

        // Unmap buffer and mark as ready
        buffer.unmap()
        isMappingRef.current = false
      }).catch(() => {
        isMappingRef.current = false
      })
      
    } catch (error) {
      isMappingRef.current = false
    }
  })
  
  return null
}